import React, { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { ImagePlus, Loader2, Star, ChevronLeft, ChevronRight, Trash2 } from "lucide-react";
import { adminApi, apiError } from "./api";
import { imgUrl } from "../lib/api";

export function ImageUploader({ images, coverIndex, onChange }) {
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");

  const onDrop = useCallback(async (accepted) => {
    if (!accepted.length) return;
    setError("");
    setBusy(true);
    setProgress(0);
    try {
      const fd = new FormData();
      accepted.forEach((f) => fd.append("files", f));
      const { data } = await adminApi.post("/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => setProgress(e.total ? Math.round((e.loaded / e.total) * 100) : 0),
      });
      const next = [...images, ...data.files.map((f) => f.path)];
      onChange(next, coverIndex);
    } catch (err) {
      setError(apiError(err, "Upload failed. Please try again."));
    } finally {
      setBusy(false);
    }
  }, [images, coverIndex, onChange]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "image/*": [] },
    multiple: true,
    disabled: busy,
  });

  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= images.length) return;
    const next = [...images];
    [next[i], next[j]] = [next[j], next[i]];
    let cover = coverIndex;
    if (coverIndex === i) cover = j;
    else if (coverIndex === j) cover = i;
    onChange(next, cover);
  };

  const remove = async (i) => {
    const path = images[i];
    const next = images.filter((_, idx) => idx !== i);
    let cover = coverIndex;
    if (coverIndex === i) cover = 0;
    else if (coverIndex > i) cover = coverIndex - 1;
    onChange(next, cover);
    adminApi.delete("/uploads", { params: { path } }).catch(() => {});
  };

  return (
    <div data-testid="image-uploader">
      <div
        {...getRootProps()}
        data-testid="image-dropzone"
        className={`flex cursor-pointer flex-col items-center justify-center border-2 border-dashed px-6 py-10 text-center transition-colors ${
          isDragActive ? "border-ivory bg-white/10" : "border-ivory/25 bg-white/5 hover:border-ivory/50"
        } ${busy ? "pointer-events-none opacity-70" : ""}`}
      >
        <input {...getInputProps()} data-testid="image-file-input" />
        {busy ? <Loader2 className="h-7 w-7 animate-spin text-ivory/70" /> : <ImagePlus className="h-7 w-7 text-ivory/70" />}
        <p className="mt-3 text-sm text-ivory">
          {busy ? `Uploading… ${progress}%` : "Drag photos here, or tap to choose from your device"}
        </p>
        <p className="mt-1 text-xs text-ivory/45">JPG, PNG or WebP · up to 15 MB each · photos are automatically optimised</p>
        {busy && (
          <div className="mt-4 h-1.5 w-full max-w-xs bg-white/10">
            <div className="h-full bg-brand transition-[width]" style={{ width: `${progress}%` }} />
          </div>
        )}
      </div>
      {error && <p className="mt-3 border-l-2 border-brand pl-3 text-sm text-ivory" data-testid="upload-error">{error}</p>}

      {images.length > 0 && (
        <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4" data-testid="image-preview-grid">
          {images.map((path, i) => (
            <div key={path} className={`relative border ${i === coverIndex ? "border-ivory" : "border-ivory/15"}`} data-testid={`photo-${i}`}>
              <img src={imgUrl(path)} alt={`Property photo ${i + 1}`} className="aspect-[4/3] w-full object-cover" />
              {i === coverIndex && (
                <span className="absolute left-2 top-2 flex items-center gap-1 bg-brand px-2 py-1 text-[0.6rem] font-bold uppercase tracking-[0.15em] text-ivory">
                  <Star className="h-3 w-3 fill-current" /> Cover
                </span>
              )}
              <div className="flex items-center justify-between border-t border-ivory/10 bg-[#1f1f1f] px-1.5 py-1.5">
                <button type="button" onClick={() => onChange(images, i)} data-testid={`photo-cover-${i}`} title="Make cover photo"
                  className={`p-1.5 ${i === coverIndex ? "text-ivory" : "text-ivory/40 hover:text-ivory"}`} aria-label={`Make photo ${i + 1} the cover`}>
                  <Star className={`h-4 w-4 ${i === coverIndex ? "fill-current" : ""}`} />
                </button>
                <div className="flex items-center">
                  <button type="button" onClick={() => move(i, -1)} disabled={i === 0} data-testid={`photo-left-${i}`} className="p-1.5 text-ivory/50 hover:text-ivory disabled:opacity-30" aria-label="Move photo earlier">
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <button type="button" onClick={() => move(i, 1)} disabled={i === images.length - 1} data-testid={`photo-right-${i}`} className="p-1.5 text-ivory/50 hover:text-ivory disabled:opacity-30" aria-label="Move photo later">
                    <ChevronRight className="h-4 w-4" />
                  </button>
                  <button type="button" onClick={() => remove(i)} data-testid={`photo-remove-${i}`} className="p-1.5 text-ivory/50 hover:text-ivory" aria-label="Remove photo">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <p className="mt-3 text-xs text-ivory/45">Photos are stored in secure cloud storage and stay available after redeployments.</p>
    </div>
  );
}
